import React from "react";
import { useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Loader2, Zap, TrendingUp, Activity, Clock } from "lucide-react";
import { format, fromUnixTime } from "date-fns";

export function UsageTab() {
  const subscriptionData = useQuery(api.subscriptions.getOrganizationSubscription, {});
  const usageData = useQuery(api.subscriptions.getUsageHistory, { days: 30 });

  if (subscriptionData === undefined || usageData === undefined) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const hasCreditLimit = (subscriptionData?.usage?.creditLimit ?? 0) > 0;
  const usagePercent = hasCreditLimit && subscriptionData?.usage
    ? Math.min((subscriptionData.usage.creditsUsed / subscriptionData.usage.creditLimit) * 100, 100)
    : 0;
  const remainingPercent = hasCreditLimit ? Math.max(0, 100 - usagePercent) : 0;

  // Map database operation types to colors
  // Database stores: QA_RETRIEVAL, SIMPLE_ACTION, COMPLEX_ACTION, WORKFLOW_STEP, TEMPLATE_FILL, PROACTIVE_ALERT
  const operationColors: Record<string, string> = {
    QA_RETRIEVAL: "bg-blue-500",
    SIMPLE_ACTION: "bg-green-500",
    COMPLEX_ACTION: "bg-purple-500",
    WORKFLOW_STEP: "bg-orange-500",
    TEMPLATE_FILL: "bg-amber-500",
    PROACTIVE_ALERT: "bg-pink-500",
  };

  // Human-readable labels for operation types
  const operationLabels: Record<string, string> = {
    QA_RETRIEVAL: "Q&A",
    SIMPLE_ACTION: "Simple Action",
    COMPLEX_ACTION: "Complex Action",
    WORKFLOW_STEP: "Document Generation",
    TEMPLATE_FILL: "Template Fill",
    PROACTIVE_ALERT: "Proactive Alert",
  };

  return (
    <div className="space-y-6">
      {/* Current Period Usage */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Zap className="h-5 w-5 text-yellow-500" />
            Current Period Usage
          </CardTitle>
          <CardDescription>
            {subscriptionData?.usage && (
              <span>
                {(() => {
                  const start = subscriptionData.subscription?.currentPeriodStart
                    ? new Date(subscriptionData.subscription.currentPeriodStart * 1000)
                    : new Date(
                        subscriptionData.usage.periodStart + new Date().getTimezoneOffset() * 60000
                      );

                  const end = subscriptionData.subscription?.currentPeriodEnd
                    ? new Date(subscriptionData.subscription.currentPeriodEnd * 1000)
                    : new Date(
                        subscriptionData.usage.periodEnd + new Date().getTimezoneOffset() * 60000
                      );

                  return `${format(start, "MMM d")} - ${format(end, "MMM d, yyyy")}`;
                })()}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Main Usage Meter */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Credits Used</span>
              <span className="text-sm text-gray-600">
                {subscriptionData?.usage?.creditsUsed.toLocaleString() || 0} /{" "}
                {subscriptionData?.usage?.creditLimit.toLocaleString() || 0}
              </span>
            </div>
            <Progress value={usagePercent} className="h-3" />
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{usagePercent.toFixed(1)}% used</span>
              <span>{remainingPercent.toFixed(1)}% remaining</span>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 border-t pt-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {subscriptionData?.usage?.creditsUsed.toLocaleString() || 0}
              </div>
              <div className="text-xs text-gray-500">Credits Used</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {Math.max(0, (
                  (subscriptionData?.usage?.creditLimit ?? 0) -
                  (subscriptionData?.usage?.creditsUsed ?? 0)
                )).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Credits Remaining</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{usageData?.logs?.length || 0}</div>
              <div className="text-xs text-gray-500">Operations</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top-Up Credits */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Zap className="h-5 w-5 text-amber-500" />
            Top-Up Credits
          </CardTitle>
          <CardDescription>
            Non-expiring credits that are used when your monthly limit is reached
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="py-6 text-center">
            <div className="mb-2 text-4xl font-bold text-gray-900">
              {subscriptionData?.organization?.purchasedCredits?.toLocaleString() || 0}
            </div>
            <p className="text-sm text-gray-500">Available credits</p>
          </div>
        </CardContent>
      </Card>

      {/* Usage Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            Usage Breakdown
          </CardTitle>
          <CardDescription>Credits consumed by operation type (last 30 days)</CardDescription>
        </CardHeader>
        <CardContent>
          {!usageData?.summary?.byOperationType ||
          Object.keys(usageData.summary.byOperationType).length === 0 ? (
            <div className="py-8 text-center">
              <Activity className="mx-auto mb-4 h-12 w-12 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900">No usage data yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Start using AI features to see your usage breakdown
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(usageData.summary.byOperationType).map(([operationType, credits]) => {
                const percentage = usageData.summary?.totalCredits
                  ? ((credits as number) / usageData.summary.totalCredits) * 100
                  : 0;

                return (
                  <div key={operationType} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">
                        {operationLabels[operationType] || operationType}
                      </span>
                      <span className="text-gray-500">
                        {(credits as number).toLocaleString()} credits
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-100">
                      <div
                        className={`h-2 rounded-full ${operationColors[operationType] || "bg-gray-400"}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5 text-gray-500" />
            Recent Activity
          </CardTitle>
          <CardDescription>Latest credit-consuming operations</CardDescription>
        </CardHeader>
        <CardContent>
          {!usageData?.logs || usageData.logs.length === 0 ? (
            <div className="py-8 text-center">
              <Clock className="mx-auto mb-4 h-12 w-12 text-gray-300" />
              <p className="text-sm text-gray-500">No recent activity</p>
            </div>
          ) : (
            <div className="space-y-3">
              {usageData.logs.slice(0, 10).map((log: any) => (
                <div
                  key={log._id}
                  className="flex items-center justify-between border-b py-2 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-2 w-2 rounded-full ${operationColors[log.operationType] || "bg-gray-400"}`}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {operationLabels[log.operationType] || log.operationType}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(log.timestamp).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        }) +
                          ", " +
                          new Date(log.timestamp).toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-gray-900">{log.cost} credits</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
